import { createElement, useEffect, useRef, useState } from 'react'

/**
 * Hook to detect if a scrollable container has been scrolled past the top.
 * Uses a sentinel element ref to detect scroll position via IntersectionObserver
 * with a polling fallback for reliability.
 *
 * @returns An object containing:
 *   - sentinelElement: React element to render before the sticky content
 *   - isScrolled: boolean indicating if the scroll container has been scrolled past the top
 */
export function useIsScrolled(): {
  sentinelElement: React.ReactElement
  isScrolled: boolean
} {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    // Find the scrollable parent container for IntersectionObserver root
    const findScrollContainer = (element: HTMLElement): HTMLElement | null => {
      let current: HTMLElement | null = element.parentElement

      while (current) {
        const style = window.getComputedStyle(current)
        if (
          (style.overflow === 'auto' ||
            style.overflow === 'scroll' ||
            style.overflowY === 'auto' ||
            style.overflowY === 'scroll') &&
          current.scrollHeight > current.clientHeight
        ) {
          return current
        }

        // Check direct children for scrollable elements
        for (const child of Array.from(current.children)) {
          if (child instanceof HTMLElement) {
            const childStyle = window.getComputedStyle(child)
            if (
              (childStyle.overflow === 'auto' ||
                childStyle.overflow === 'scroll' ||
                childStyle.overflowY === 'auto' ||
                childStyle.overflowY === 'scroll') &&
              child.scrollHeight > child.clientHeight
            ) {
              return child
            }
          }
        }

        current = current.parentElement
      }

      return null
    }

    const scrollContainer = findScrollContainer(sentinel)

    // Use IntersectionObserver to detect when sentinel is scrolled out of view
    const observer = new IntersectionObserver(
      (entries) => {
        // When sentinel is not intersecting (scrolled past), show border
        // Use the last entry to handle cases where multiple entries are reported
        const entry = entries[entries.length - 1]
        if (entry && sentinelRef.current) {
          setIsScrolled(!entry.isIntersecting)
        }
      },
      {
        root: scrollContainer, // Use scroll container as root if found, otherwise viewport
        rootMargin: '0px',
        threshold: 0,
      },
    )

    observer.observe(sentinel)

    return () => {
      observer.disconnect()
    }
  }, [])

  const sentinelElement = createElement('div', {
    ref: sentinelRef,
    'aria-hidden': 'true',
  })

  return { sentinelElement, isScrolled }
}
